import Link from '@pinecast/common/Link';
import {BlockSkeleton, InlineSkeleton} from '@pinecast/common/Skeleton';
import {DashboardTitle} from '@pinecast/common/Text';
import type {QueryResult} from '@pinecast/gql';
import {useManagedMutation} from '@pinecast/gql';
import type {
  CreateTipSetupSession,
  CreateTipSetupSessionVariables,
} from '@pinecast/gql/__generated_tj__/CreateTipSetupSession';
import type {RecurringDonation_tipJarSubscription} from '@pinecast/gql/__generated_tj__/RecurringDonation';
import type {RecurringDonationSupplemental} from '@pinecast/gql/__generated_tj__/RecurringDonationSupplemental';
import {defineMessages, FormattedMessage} from '@pinecast/i18n';
import {useCSS} from '@pinecast/styles';
import gql from 'graphql-tag';

import {stripeKey, useStripe} from '../donationPage/useStripe';
import Amex from '../icons/Amex';
import Brandless from '../icons/Brandless';
import MasterCard from '../icons/MasterCard';
import Visa from '../icons/Visa';

const messages = defineMessages({
  title: {
    id: 'tj-spa.RecurringDonationPaymentMethod.title',
    description: 'Title of the payment method section',
    defaultMessage: 'Payment method',
  },
  button_changePaymentMethod: {
    id: 'tj-spa.RecurringDonationPaymentMethod.button.changePaymentMethod',
    description: 'Button to change the payment method for this subscription',
    defaultMessage: 'Change payment method',
  },
  expiration: {
    id: 'tj-spa.RecurringDonationPaymentMethod.expiration',
    description: 'Label for credit card expiration date',
    defaultMessage: 'Expires',
  },
});

const CREATE_TIP_SETUP_SESSION = gql`
  mutation CreateTipSetupSession($slug: ID!) {
    createSetupCheckoutSession(podcastSlug: $slug) {
      sessionId
    }
  }
`;

type Props = {
  subscription: RecurringDonation_tipJarSubscription;
  supplemental: QueryResult<RecurringDonationSupplemental>;
};

const RecurringDonationPaymentMethod = ({
  subscription,
  supplemental,
}: Props) => {
  const css = useCSS();
  const stripe = useStripe(stripeKey, subscription.podcast.tipJarAccountId!);

  const createTipSetupSession = useManagedMutation<
    CreateTipSetupSession,
    CreateTipSetupSessionVariables
  >(CREATE_TIP_SETUP_SESSION, {
    variables: {slug: subscription.podcast.slug},
  });

  return (
    <>
      <DashboardTitle>
        <FormattedMessage {...messages.title} />
      </DashboardTitle>
      <div
        className={css({
          display: 'grid',
          gridTemplateColumns: '20px 1fr',
          gap: '12px',
        })}
      >
        {supplemental.data?.tipJarSubscription ? (
          ((
            {
              visa: <Visa />,
              amex: <Amex />,
              mastercard: <MasterCard />,
            } as Record<string, React.ReactNode>
          )[supplemental.data.tipJarSubscription.paymentCard.brand] ?? (
            <Brandless />
          ))
        ) : (
          <BlockSkeleton style={{width: 20, height: 20}} />
        )}
        <div className={css({display: 'flex', flexDirection: 'column'})}>
          <strong>
            {'•••• '}
            {supplemental.data?.tipJarSubscription ? (
              supplemental.data.tipJarSubscription.paymentCard.last4
            ) : (
              <InlineSkeleton width="32px" />
            )}
          </strong>
          <span>
            <FormattedMessage
              {...messages.expiration}
              style={{marginRight: '4px'}}
            />
            {supplemental.data?.tipJarSubscription ? (
              supplemental.data.tipJarSubscription.paymentCard.expMonth
            ) : (
              <InlineSkeleton width="24px" />
            )}
            {' / '}
            {supplemental.data?.tipJarSubscription ? (
              supplemental.data.tipJarSubscription.paymentCard.expYear
            ) : (
              <InlineSkeleton width="32px" />
            )}
            {' · '}
            <Link
              onClick={async () => {
                const result = await createTipSetupSession.action();
                if (!result) {
                  return;
                }
                (await stripe)?.redirectToCheckout({
                  sessionId: result.createSetupCheckoutSession!.sessionId,
                });
              }}
            >
              <FormattedMessage {...messages.button_changePaymentMethod} />
            </Link>
          </span>
        </div>
      </div>
    </>
  );
};

export default RecurringDonationPaymentMethod;
